"use client"

import SelectImageButton from "@/(admin)/components/image/select-image-button"
import { FormSection, FormShell, FormSkeleton } from "@/(admin)/components/form"
import { useEventUpsert } from "@/(admin)/modules/event/hooks/useEventUpsert"
import AccessDenied from "@/components/custom-access-denied"
import { EVENTS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import { MediaData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { DatePicker, Form, Input, Select } from "antd"
import dynamic from "next/dynamic"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import Publish from "./Publish"

const CKEditorComponent = dynamic(() => import("@/lib/config/lib/Ckeditor"), { ssr: false })

const { TextArea } = Input

export default function EventUpsertForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [submitting, setSubmitting] = useState(false)
  const { hasPermission: checkPermission } = useCheckPermission()
  const [isFormReady, setIsFormReady] = useState(false);
  const {
    form,
    isEditMode,
    websiteOptions,
    subscriberOptions,
    loadingWebsite,
    loadingSubscriber,
    loadingDetail,
    selectedCoverImage,
    setSelectedCoverImage,
    onFinish,
    status,
    setStatus,
    language,
    setLanguage,
    publishDate,
    setPublishDate,
  } = useEventUpsert({
    onDataLoaded: () => {
      setIsFormReady(true);
    }
  })

  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      status,
      language,
      publishDate,
      selectedCoverImage: selectedCoverImage.length > 0 ? selectedCoverImage[0].id : null,
    }
  }, [form, status, language, publishDate, selectedCoverImage])

  const { checkChange, commitSnapshot } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });


  useEffect(() => {
    checkChange();
  }, [status, language, publishDate, selectedCoverImage, checkChange]);

  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const requiredPermission = isEditMode ? "update" : "create"

  if (!checkPermission("event", requiredPermission)) {
    return <AccessDenied />
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      const success = await onFinish(values)
      if (success) {
        commitSnapshot();
        if (!isEditMode) {
          router.push(createPath(EVENTS.LIST.PATH, searchParams))
        }
      }
    } catch (error) {
      console.error("Validation failed:", error)
    } finally {
      setSubmitting(false)
    }
  }

  if (isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={3} sidebarCards={1} />
  }

  const crumbs = [
    { title: t("Sự kiện"), href: createPath(EVENTS.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin sự kiện")} collapsible={false}>
      <Form.Item
        name="name"
        label={<span className="text-base font-medium">{t("Name")}</span>}
        rules={[{ required: true, message: t("Please enter event name") }]}
      >
        <Input placeholder={t("Enter event name")} />
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="location"
          label={<span className="text-base font-medium">{t("Event Location")}</span>}
          rules={[{ required: true, message: t("Please enter event location") }]}
        >
          <Input placeholder={t("Enter event location")} />
        </Form.Item>

        <Form.Item
          name="date"
          label={<span className="text-base font-medium">{t("Date")}</span>}
          rules={[{ required: true, message: t("Please select event date") }]}
        >
          <DatePicker showTime className="w-full" format="HH:mm DD/MM/YYYY" placeholder={t("Select date")} />
        </Form.Item>
      </div>

      <Form.Item
        name="contact"
        label={<span className="text-base font-medium">{t("Contact")}</span>}
        rules={[{ required: true, message: t("Please enter contact information") }]}
      >
        <Input placeholder={t("Enter contact information")} />
      </Form.Item>

      <Form.Item
        name="shortDescription"
        rules={[{ required: true, message: t("Vui lòng nhập mô tả ngắn") }]}
        label={<span className="text-base font-medium">{t("Short Description")}</span>}
      >
        <TextArea rows={3} placeholder={t("Enter short description")} />
      </Form.Item>

      <Form.Item name="detail" label={<span className="text-base font-medium">{t("Detail")}</span>}>
        <CKEditorComponent
          value={form.getFieldValue("detail")}
          onChange={data => {
            form.setFieldValue("detail", data)
            checkChange()
          }}
        />
      </Form.Item>

      <Form.Item name="websites" label={<span className="text-base font-medium">{t("Websites")}</span>}>
        <Select
          mode="multiple"
          placeholder={t("Select website")}
          options={websiteOptions}
          loading={loadingWebsite}
          allowClear
          showSearch
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      <Form.Item
        name="subscribers"
        label={<span className="text-base font-medium">{t("Subscribers")}</span>}
        className="!mb-0"
      >
        <Select
          mode="multiple"
          placeholder={t("Select subscriber")}
          options={subscriberOptions}
          loading={loadingSubscriber}
          allowClear
          showSearch
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
    </FormSection>
  )

  const sidebar = (
    <>
      <Publish
        onSubmit={form.submit}
        type={isEditMode ? "edit" : "create"}
        status={status}
        setStatus={setStatus}
        language={language}
        setLanguages={setLanguage}
        setPublishDate={setPublishDate}
        isCreateEdit={!isEditMode}
        loading={submitting || loadingDetail}
      />
      <FormSection title={t("Cover Image")} compact>
        <Form.Item
          name="cover"
          rules={[{ required: true, message: t("Please upload cover image") }]}
          className="!mb-0"
        >
          <SelectImageButton
            selectedMedia={selectedCoverImage}
            setSelectedMedia={(value: MediaData[]) => {
              setSelectedCoverImage(value)
              form.setFieldValue("cover", value.length > 0 ? value[0].id : null)
              checkChange()
            }}
            selectMode="single"
          />
        </Form.Item>
      </FormSection>
    </>
  )

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ locale: "vi" }}
      disabled={loadingDetail}
      onFinish={handleSubmit}
      onValuesChange={checkChange}
    >
      <FormShell
        title={isEditMode ? t("Edit Event") : t("Add New Event")}
        crumbs={crumbs}
        main={main}
        sidebar={sidebar}
      />
    </Form>
  )
}